import React from "react";
import { cn } from "@src/lib/utils/cn";
import Text from "@src/lib/ui/Text";
import { Button } from "@src/lib/ui/Button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@src/lib/ui/Tooltip";
import ArrowUpIcon from "@src/lib/icons/18px/arrow-up";
import ArrowDownIcon from "@src/lib/icons/18px/arrow-down";
import XIcon from "@src/lib/icons/18px/x";
import ChevronLeftIcon from "@src/lib/icons/18px/chevron-left";
import ArrowUpRightIcon from "@src/lib/icons/18px/arrow-up-right";
import { useDetailSidebarContext } from "@src/lib/contexts/DetailSidebarContext";
import { useTableDetailSidebarNavigationEvents } from "@src/features/sidebars/hooks/useTableDetailSidebarNavigationEvents";
import {
  DetailSidebarType,
  DetailSidebarTypeToTitle,
} from "@src/features/sidebars/detailSidebar";
import { Route } from "nextjs-routes";
import NextLink from "next/link";

interface SidebarHeaderProps {
  className?: string;
  detailPageRoute?: Route;
}

type SidebarTitleProps = {
  title: string;
  icon?: React.ReactNode;
  sidebarType?: DetailSidebarType;
};

export const SidebarTitle: React.FC<SidebarTitleProps> = ({
  title,
  icon,
  sidebarType: sidebarTypeProp,
}) => {
  // Try to get from context, fall back to prop
  let sidebarType: DetailSidebarType | null = null;
  try {
    const context = useDetailSidebarContext();
    sidebarType =
      sidebarTypeProp || (context.sidebarType as DetailSidebarType | null);
  } catch {
    // Context not available, use prop
    sidebarType = sidebarTypeProp || null;
  }
  const subtitle = sidebarType ? DetailSidebarTypeToTitle[sidebarType] : "";
  return (
    <div className="flex flex-col p-4 pt-6 pb-2">
      <Text variant="compact-title" className="text-gray-7">
        {subtitle}
      </Text>
      <div className="my-1 flex items-center gap-2">
        {icon && (
          <div className="flex size-[30px] items-center justify-center rounded-md bg-gray-2">
            {icon}
          </div>
        )}
        <Text variant="display-bold">{title}</Text>
      </div>
    </div>
  );
};

type SidebarContentProps = {
  children: React.ReactNode;
};

export const SidebarContent: React.FC<SidebarContentProps> = ({ children }) => {
  return (
    <div className="flex flex-1 flex-col self-stretch overflow-auto p-4 pb-0">
      {children}
    </div>
  );
};

const SidebarHeader: React.FC<SidebarHeaderProps> = ({
  className,
  detailPageRoute: detailPagePath,
}) => {
  const {
    canNavigateNext,
    canNavigatePrevious,
    closeSidebar,
    canGoBack,
    goBack,
    rootNavigationContext,
    objectId,
    sidebarType,
    sidebarHistory,
  } = useDetailSidebarContext();

  const { emit } = useTableDetailSidebarNavigationEvents();

  // Generate route dynamically if not provided
  const detailPageRoute =
    detailPagePath ||
    (objectId && sidebarType
      ? {
          pathname: "/detail/[object-type]/[id]" as const,
          query: {
            "object-type": sidebarType,
            id: objectId,
          },
        }
      : undefined);

  // Get the object type we're going back to
  const previousSidebarType =
    sidebarHistory.length > 0
      ? sidebarHistory[sidebarHistory.length - 1]?.sidebarType
      : null;
  const backToObjectType = previousSidebarType
    ? DetailSidebarTypeToTitle[previousSidebarType]
    : null;

  return (
    <div
      className={cn(
        " bg-background p-2 px-2 border-b border-border",
        className,
      )}
    >
      {/* Header Row */}
      <div className="flex items-center justify-between">
        <div className="flex min-w-0 flex-1 items-center">
          {/* Back Button */}

          {canGoBack && (
            <Tooltip>
              <Button
                variant="ghost"
                size="sm"
                onClick={goBack}
                className="mr-2 h-8 shrink-0 px-2"
              >
                <div className="flex items-center gap-1">
                  <ChevronLeftIcon className="size-4 text-gray-9" />
                  {backToObjectType && (
                    <Text variant="compact-description" className="text-gray-7">
                      Back to {backToObjectType}
                    </Text>
                  )}
                </div>
              </Button>
            </Tooltip>
          )}
          {/* Navigation Controls */}
          {rootNavigationContext?.tableId &&
            !canGoBack &&
            sidebarType !== DetailSidebarType.NewStoreOpeningsList && (
              <div className="flex items-center">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-md"
                      onClick={() => {
                        emit("NAVIGATE_PREVIOUS");
                      }}
                      disabled={!canNavigatePrevious}
                    >
                      <ArrowUpIcon className="size-4 text-gray-9" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Previous row</p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-md"
                      onClick={() => {
                        emit("NAVIGATE_NEXT");
                      }}
                      disabled={!canNavigateNext}
                    >
                      <ArrowDownIcon className="size-4 text-gray-9" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Next row</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            )}
        </div>
        {/* Expand Button */}
        {detailPageRoute &&
          sidebarType !== DetailSidebarType.NewStoreOpeningsList && (
            <Tooltip>
              <TooltipTrigger asChild>
                <NextLink
                  href={detailPageRoute}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="ghost" size="icon-md">
                    <ArrowUpRightIcon className="size-4 text-gray-9" />
                  </Button>
                </NextLink>
              </TooltipTrigger>
              <TooltipContent>
                <p>Open as fullscreen page in new tab</p>
              </TooltipContent>
            </Tooltip>
          )}
        <div className="flex items-center gap-1">
          {/* Close Button */}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon-md" onClick={closeSidebar}>
                <XIcon className="size-4 text-gray-9" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Close sidebar</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default SidebarHeader;
